import { Table, TableCell, TableRow } from 'components/common/minimalTable'
import { OrderProductType, OrderType } from 'types/order'
import { convertTo12HourFormat } from 'utils/helpers'
import { ORDER_STATUS_CLASSES } from 'utils/constants'
import { roundToFourDecimals } from 'utils/helpers/round'

const ProductRow = ({
  _id,
  description,
  barcode,
  qty,
  systemPrice,
  unit_price_inclv_tax,
  amountExclVat,
  unitVat,
  uom,
}: OrderProductType) => (
  <TableRow key={_id}>
    <TableCell>
      {description && <p className="mb-1 font-bold">{description}</p>}
      <strong>SKU:</strong> {barcode}
      <p>
        <strong>UOM:</strong> {uom || 'NA'}
      </p>
    </TableCell>
    <TableCell>{qty}</TableCell>
    <TableCell>{roundToFourDecimals(systemPrice) ?? 'NA'}</TableCell>
    <TableCell>{roundToFourDecimals(unit_price_inclv_tax) ?? 'NA'}</TableCell>
    <TableCell>{roundToFourDecimals(amountExclVat) ?? 'NA'}</TableCell>
    <TableCell>
      {unitVat !== undefined && unitVat !== null
        ? roundToFourDecimals(unitVat) ?? 0
        : 'NA'}
    </TableCell>
    <TableCell>
      {unitVat !== undefined && unitVat !== null
        ? roundToFourDecimals(unitVat * qty) ?? 0
        : 'NA'}
    </TableCell>
    <TableCell>
      {roundToFourDecimals(Number(unit_price_inclv_tax) * qty)}
    </TableCell>
  </TableRow>
)

// Each field: label on top in clear gray, value below in dark bold text
const Field = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <div className="flex flex-col gap-0.5">
    <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">
      {label}
    </span>
    <span className="text-[15px] font-semibold text-gray-800">
      {value ?? <span className="font-normal text-gray-400">N/A</span>}
    </span>
  </div>
)

// Left-accented section title with a colored bar
const SectionTitle = ({
  title,
  color = 'bg-blue-500',
}: {
  title: string
  color?: string
}) => (
  <div className="mb-4 flex items-center gap-2.5">
    <div className={`h-5 w-1 rounded-full ${color}`} />
    <span className="text-[13px] font-bold uppercase tracking-widest text-gray-600">
      {title}
    </span>
  </div>
)

const Card = ({
  children,
  className = '',
}: {
  children: React.ReactNode
  className?: string
}) => (
  <div
    className={`rounded-xl border border-gray-100 bg-white p-5 shadow-sm ${className}`}
  >
    {children}
  </div>
)

const OpenOrderDetailsSection = ({ order }: { order: OrderType }) => {
  const {
    order_no,
    Order_shortcode,
    Store_code,
    order_place_datetime,
    Planned_DeliveryDate,
    Delivery_provider,
    service_fee,
    delivery_fee,
    customer_Name,
    customer_Address,
    customer_Address2,
    Area,
    City,
    Contact,
    products = [],
    payment = [],
    Country,
    invoiceNo,
    storeName,
    Aggregator_Code,
    Aggregator_Name,
    lastMile,
    createdAt,
    status,
    Prepared_by,
    empId,
    updatedAt,
  } = order

  return (
    <div className="flex flex-col gap-3">
      {/* ── Header ── */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <h3 className="title">Order Details</h3>
          <span className="rounded-lg bg-gray-800 px-3 py-1 text-sm font-semibold text-white shadow">
            {order_no}
          </span>
        </div>
        <div className="flex items-center gap-2 rounded-xl border border-gray-100 bg-white px-4 py-2 shadow-sm">
          <span className="text-[11px] font-bold uppercase tracking-widest text-gray-400">
            Status
          </span>
          <span
            className={`rounded-lg px-3 py-1 text-sm font-bold ${
              ORDER_STATUS_CLASSES[status!]
            }`}
          >
            {status}
          </span>
        </div>
      </div>

      {/* ── Order Info ── */}
      <Card>
        <SectionTitle title="Order Info" color="bg-blue-500" />
        <div className="grid grid-cols-2 gap-x-6 gap-y-5 md:grid-cols-3">
          <Field label="Invoice No" value={invoiceNo} />
          <Field label="Order Shortcode" value={Order_shortcode} />
          <Field label="Prepared By" value={Prepared_by} />
          <Field label="Employee ID" value={empId} />
          <Field
            label={`Order Date`}
            value={
              order_place_datetime
                ? convertTo12HourFormat(order_place_datetime)
                : null
            }
          />
          <Field
            label={`Planned Delivery Date`}
            value={
              Planned_DeliveryDate
                ? convertTo12HourFormat(Planned_DeliveryDate)
                : null
            }
          />
          <Field
            label="Order Posting Date"
            value={createdAt ? convertTo12HourFormat(createdAt) : null}
          />
          <Field
            label="Last Updated (Localize)"
            value={updatedAt ? convertTo12HourFormat(updatedAt) : null}
          />
        </div>
      </Card>

      {/* ── Store & Delivery side by side ── */}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <Card>
          <SectionTitle title="Store & Channel" color="bg-violet-500" />
          <div className="grid grid-cols-2 gap-x-6 gap-y-5">
            <Field label="Store Name" value={storeName} />
            <Field label="Store Code" value={Store_code} />
            <Field label="Aggregator Name" value={Aggregator_Name} />
            <Field label="Aggregator Code" value={Aggregator_Code} />
          </div>
        </Card>

        <Card>
          <SectionTitle title="Delivery & Fees" color="bg-emerald-500" />
          <div className="grid grid-cols-2 gap-x-6 gap-y-5">
            <Field label="Delivery Provider" value={Delivery_provider} />
            <Field label="Last Mile" value={lastMile} />
            <Field label="Country" value={Country} />
            <Field
              label="Service Fee"
              value={roundToFourDecimals(service_fee)}
            />
            <Field
              label="Delivery Fee"
              value={roundToFourDecimals(delivery_fee)}
            />
          </div>
        </Card>
      </div>

      {/* ── Customer Details ── */}
      <Card>
        <SectionTitle title="Customer Details" color="bg-orange-400" />
        <div className="grid grid-cols-2 gap-x-6 gap-y-5 md:grid-cols-3">
          <Field label="Name" value={customer_Name} />
          <Field label="Contact" value={Contact} />
          <Field label="Area" value={Area} />
          <Field label="City" value={City} />
          <Field label="Address" value={customer_Address} />
          <Field label="Address 2" value={customer_Address2} />
        </div>
      </Card>

      {/* ── Products ── */}
      <Card>
        <SectionTitle title="Products" color="bg-rose-400" />
        {products?.length > 0 ? (
          <Table
            headers={[
              'Barcode',
              'Quantity',
              'Grandiose Price',
              'Unit Price (Incl. Tax)',
              'Unit Price (Excl. Tax)',
              'Unit VAT',
              'Total VAT',
              'Total',
            ]}
          >
            {products.map((product) => (
              <ProductRow key={product._id} {...product} />
            ))}
          </Table>
        ) : (
          <p className="text-sm text-gray-400">No product details available</p>
        )}
      </Card>

      {/* ── Payments ── */}
      <Card>
        <SectionTitle title="Payments" color="bg-teal-500" />
        {payment?.length > 0 ? (
          <Table headers={['Payment Type', 'Amount']}>
            {payment.map((pay) => (
              <TableRow key={pay._id}>
                <TableCell>{pay.payment_type}</TableCell>
                <TableCell>{pay.amount?.toFixed(2)}</TableCell>
              </TableRow>
            ))}
          </Table>
        ) : (
          <p className="text-sm text-gray-400">No payment details available</p>
        )}
      </Card>
    </div>
  )
}

export default OpenOrderDetailsSection
